import type { ExpenseCreatePaymentIntent, ExpenseSettleMethod } from '../components/expenseSettlementMethods';
import type { ExpenseCategorySlug } from '../domain/expenseCategories';
import type { ExpenseFiscalInvoiceInput } from '../domain/expenseTypes';
import { validateExpenseInput } from '../domain/expenseValidation';
import { validateExpenseFiscalInvoice } from '../domain/expenseFiscalInvoice';

export type ExpenseRegisterAttachmentInput = {
  fileName: string;
  mimeType: string;
  base64: string;
  fileSize: number;
};

export type ExpenseRegisterSubmitInput = {
  categorySlug: ExpenseCategorySlug;
  amountText: string;
  description: string;
  receiptRef: string;
  supplierId: string | null;
  expenseDate: string;
  dueDate: string | null;
  attachment: ExpenseRegisterAttachmentInput | null;
  paymentIntent: ExpenseCreatePaymentIntent;
  settleMethod: ExpenseSettleMethod;
  settleNote: string;
  paidDate: string;
  /** Comprobante fiscal recibido (Libro IVA compras) — null = gasto informal. */
  fiscal?: ExpenseFiscalInvoiceInput | null;
};

export type ExpenseRegisterValidationError = {
  title: string;
  message: string;
};

export function validateExpenseRegisterBusinessRules(
  input: ExpenseRegisterSubmitInput,
  ctx: { activeCashSessionId: string | null },
  amount: number
): ExpenseRegisterValidationError | null {
  const fieldError = validateExpenseInput({
    categorySlug: input.categorySlug,
    amount,
    description: input.description,
    expenseDate: input.expenseDate,
    dueDate: input.dueDate,
  });
  if (!fieldError.ok) {
    return { title: 'Revisá los datos', message: fieldError.message };
  }

  if (input.paymentIntent === 'pay_now' && input.settleMethod === 'cash_drawer' && !ctx.activeCashSessionId) {
    return {
      title: 'Caja cerrada',
      message: 'Abrí un turno de caja para pagar en efectivo desde la caja.',
    };
  }

  if (input.fiscal) {
    const fiscalError = validateExpenseFiscalInvoice(input.fiscal);
    if (fiscalError) {
      return { title: 'Revisá el comprobante fiscal', message: fiscalError };
    }
  }

  if (
    input.paymentIntent === 'pay_now' &&
    input.settleMethod === 'other' &&
    !input.settleNote.trim()
  ) {
    return {
      title: 'Nota requerida',
      message: 'Indicá cuándo y cómo se pagó en efectivo de la caja.',
    };
  }

  return null;
}

export function shouldConfirmCashDrawerOverExpected(input: ExpenseRegisterSubmitInput): boolean {
  return input.paymentIntent === 'pay_now' && input.settleMethod === 'cash_drawer';
}

export function expenseRegisterSuccessCopy(paymentIntent: ExpenseRegisterSubmitInput['paymentIntent']): {
  title: string;
  message: string;
} {
  if (paymentIntent === 'pay_now') {
    return { title: 'Gasto pagado', message: 'Quedó registrado y pagado.' };
  }
  return { title: 'Gasto registrado', message: 'Quedó pendiente de pago.' };
}
